import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import '../styles/communication.css';
import { UserContext } from '../contexts/UserContext';

const CommunicationPage = () => {
    const navigate = useNavigate();
    const { redirectToLogin } = useContext(UserContext);
    const [message, setMessage] = useState('');
    const [userStats, setUserStats] = useState({
        wallet_balance: 0,
        total_followers: 0,
        currency: 'Zaires'
    });
    const [costPerSMS, setCostPerSMS] = useState(0.20);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [campaignStatus, setCampaignStatus] = useState(null);
    const [statusPolling, setStatusPolling] = useState(null);

    // Fetch user stats and SMS settings
    const fetchUserStats = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(
                `${config.backendUrl}/communications/stats/`,
                {
                    headers: {
                        'Authorization': `Token ${localStorage.getItem('authToken')}`
                    }
                }
            );

            setUserStats({
                wallet_balance: response.data.wallet_balance,
                total_followers: response.data.total_followers,
                currency: response.data.currency || 'Zaires'
            });

            // Fetch SMS pricing
            try {
                const settingsResponse = await axios.get(
                    `${config.backendUrl}/communications/sms-settings/`
                );
                const price = parseFloat(settingsResponse.data.cost_per_sms);
                setCostPerSMS(price);
            } catch (err) {

            }

        } catch (error) {
            console.error('Error fetching stats:', error);
            if (error.response?.status === 401) {
                redirectToLogin('/login');
            } else {
                setError('Erreur lors du chargement des informations');
            }
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        fetchUserStats();
    }, [fetchUserStats]);

    // Calculate total cost
    const totalCost = userStats.total_followers * costPerSMS;
    const canSend = userStats.wallet_balance >= totalCost && userStats.total_followers > 0 && message.trim().length > 0;

    // Poll campaign status
    const pollCampaignStatus = useCallback(async (campaignId) => {
        let isPolling = true;

        const poll = async () => {
            if (!isPolling) return;

            try {
                const response = await axios.get(
                    `${config.backendUrl}/campaigns/${campaignId}/status/`,
                    {
                        headers: {
                            'Authorization': `Token ${localStorage.getItem('authToken')}`
                        }
                    }
                );

                setCampaignStatus(response.data);

                // Check if campaign is completed
                if (['COMPLETED', 'FAILED', 'CANCELLED'].includes(response.data.status)) {
                    isPolling = false;
                    setSending(false);
                    if (response.data.status === 'COMPLETED') {
                        setSuccess(`Message envoyé avec succès à ${response.data.sent_count} follower(s)!`);
                        setTimeout(() => {
                            setShowProgressModal(false);
                            setMessage('');
                            fetchUserStats(); // Refresh balance
                        }, 3000);
                    }
                    return;
                }

                // Continue polling if not completed
                if (isPolling) {
                    setTimeout(poll, 2000); // Poll every 2 seconds
                }
            } catch (error) {
                console.error('Error polling status:', error);
                isPolling = false;
            }
        };

        poll();

        // Cleanup function
        return () => {
            isPolling = false;
        };
    }, [fetchUserStats]);

    // Send message to followers
    const sendMessageToFollowers = async () => {
        if (!message.trim()) {
            setError('Veuillez saisir un message');
            return;
        }

        if (userStats.wallet_balance < totalCost) {
            if (window.confirm(
                `Vous avez besoin de ${totalCost.toLocaleString()} ${userStats.currency} pour envoyer ce message à ${userStats.total_followers} client(s). Votre solde actuel: ${userStats.wallet_balance.toLocaleString()} ${userStats.currency}\n\nVoulez-vous recharger votre portefeuille?`
            )) {
                navigate('/wallet');
            }
            return;
        }

        try {
            setShowProgressModal(true);
            setSending(true);
            setError(null);
            setSuccess(null);

            const response = await axios.post(
                `${config.backendUrl}/communications/send-sms/`,
                {
                    message: message.trim()
                },
                {
                    headers: {
                        'Authorization': `Token ${localStorage.getItem('authToken')}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const campaignId = response.data.campaign?.id;
            
            if (campaignId) {
                // Start polling campaign status
                const cleanup = pollCampaignStatus(campaignId);
                setStatusPolling(() => cleanup);
            } else {
                setSending(false);
                setSuccess('Message envoyé avec succès!');
                setMessage('');
                fetchUserStats();
                setTimeout(() => {
                    setShowProgressModal(false);
                }, 2000);
            }

        } catch (error) {
            console.error('Error sending message:', error);
            setSending(false);
            setShowProgressModal(false);
            
            if (error.response?.status === 401) {
                redirectToLogin('/login');
            } else if (error.response?.status === 400) {
                setError(error.response.data.error || 'Données invalides');
            } else {
                setError('Erreur lors de l\'envoi du message');
            }
        }
    };

    // Character count and SMS count estimation
    const characterCount = message.length;
    const maxLength = 1000;
    const smsCount = Math.ceil(characterCount / 160) || 1;

    if (loading) {
        return (
            <div className="communication-page">
                <div className="communication-container">
                    <div className="skeleton-container">
                        <div className="skeleton skeleton-title" style={{ width: '60%', height: '32px', marginBottom: '10px' }}></div>
                        <div className="skeleton skeleton-text short" style={{ width: '80%', marginBottom: '30px' }}></div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                            <div className="skeleton skeleton-box" style={{ height: '120px' }}></div>
                            <div className="skeleton skeleton-box" style={{ height: '120px' }}></div>
                        </div>
                        
                        <div className="skeleton skeleton-box" style={{ height: '200px', marginBottom: '20px' }}></div>
                        <div className="skeleton skeleton-input" style={{ marginBottom: '20px' }}></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="communication-page">
            <div className="communication-container">
                <div className="page-header">
                    <h1>📱 Envoyer SMS aux Followers</h1>
                    <p className="page-subtitle">Communiquez avec tous vos clients en un seul message</p>
                </div>

                {error && (
                    <div className="alert alert-error">
                        <span>⚠️ {error}</span>
                        <button onClick={() => setError(null)}>&times;</button>
                    </div>
                )}

                {success && (
                    <div className="alert alert-success">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                            <span>✅ {success}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <button 
                                onClick={() => navigate('/sms-history')}
                                style={{
                                    padding: '6px 15px',
                                    background: 'white',
                                    color: '#28A745',
                                    border: '2px solid #28A745',
                                    borderRadius: '6px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem'
                                }}
                            >
                                Voir l'historique
                            </button>
                            <button onClick={() => setSuccess(null)}>&times;</button>
                        </div>
                    </div>
                )}

                {/* User Stats Cards */}
                <div className="stats-grid">
                    <div className="stat-card wallet-card">
                        <div className="stat-icon">💰</div>
                        <div className="stat-content">
                            <h3>Solde Portefeuille</h3>
                            <p className="stat-value">{userStats.wallet_balance.toLocaleString()} {userStats.currency}</p>
                            <button 
                                className="btn-link"
                                onClick={() => navigate('/wallet')}
                            >
                                Recharger →
                            </button>
                        </div>
                    </div>

                    <div className="stat-card followers-card">
                        <div className="stat-icon">👥</div>
                        <div className="stat-content">
                            <h3>Total Followers</h3>
                            <p className="stat-value">{userStats.total_followers}</p>
                            <span className="stat-label">clients abonnés</span>
                        </div>
                    </div>
                </div>

                {/* SMS Pricing Info */}
                <div className="pricing-section">
                    <h2>💳 Tarification SMS</h2>
                    <div className="pricing-grid">
                        <div className="pricing-card">
                            <div className="pricing-label">Coût par SMS</div>
                            <div className="pricing-value">{costPerSMS.toFixed(2)} {userStats.currency}</div>
                        </div>
                        <div className="pricing-card highlight">
                            <div className="pricing-label">Coût Total</div>
                            <div className="pricing-value">{totalCost.toLocaleString()} {userStats.currency}</div>
                            <div className="pricing-sublabel">pour {userStats.total_followers} destinataire(s)</div>
                        </div>
                    </div>
                </div>

                {/* Message Input Section */}
                <div className="message-section">
                    <h2>✍️ Votre Message</h2>
                    <div className="message-input-container">
                        <textarea
                            className="message-textarea"
                            placeholder="Écrivez votre message ici... (en français)"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            maxLength={maxLength}
                            rows={6}
                        />
                        <div className="message-info">
                            <span className="char-count">
                                {characterCount} / {maxLength} caractères
                            </span>
                            <span className="sms-count">
                                ≈ {smsCount} SMS
                            </span>
                        </div>
                    </div>

                    <div className="message-tips">
                        <p><strong>💡 Conseils:</strong></p>
                        <ul>
                            <li>Soyez clair et concis</li>
                            <li>Personnalisez votre message</li>
                            <li>Incluez un appel à l'action</li>
                            <li>Vérifiez l'orthographe avant d'envoyer</li>
                        </ul>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="action-buttons">
                    <button
                        className="btn btn-secondary"
                        onClick={() => navigate(-1)}
                        disabled={sending}
                    >
                        Annuler
                    </button>
                    <button
                        className={`btn btn-primary ${!canSend ? 'disabled' : ''}`}
                        onClick={sendMessageToFollowers}
                        disabled={!canSend || sending}
                    >
                        {sending ? '📤 Envoi en cours...' : '📨 Envoyer le Message'}
                    </button>
                </div>

                {!canSend && message.trim().length > 0 && (
                    <div className="warning-message">
                        {userStats.wallet_balance < totalCost ? (
                            <p>⚠️ Solde insuffisant. Veuillez recharger votre portefeuille.</p>
                        ) : userStats.total_followers === 0 ? (
                            <p>⚠️ Vous n'avez pas encore de followers.</p>
                        ) : null}
                    </div>
                )}
            </div>

            {/* Progress Modal */}
            {showProgressModal && (
                <div className="modal-overlay">
                    <div className="progress-modal">
                        <h3>📤 Envoi en cours...</h3>
                        {campaignStatus ? (
                            <div className="progress-content">
                                <div className="progress-bar-container">
                                    <div 
                                        className="progress-bar"
                                        style={{ width: `${campaignStatus.progress_percentage}%` }}
                                    />
                                </div>
                                <div className="progress-stats">
                                    <p><strong>Statut:</strong> {campaignStatus.status_display}</p>
                                    <p><strong>Envoyés:</strong> {campaignStatus.sent_count} / {campaignStatus.total_recipients}</p>
                                    <p><strong>Échecs:</strong> {campaignStatus.failed_count}</p>
                                    <p><strong>Progression:</strong> {campaignStatus.progress_percentage}%</p>
                                </div>
                            </div>
                        ) : (
                            <div className="skeleton-container">
                                <div className="skeleton skeleton-bar"></div>
                                <div className="skeleton skeleton-text"></div>
                                <div className="skeleton skeleton-text"></div>
                                <div className="skeleton skeleton-text"></div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CommunicationPage;
